/**
 * The status dot a dashboard card wears beside its title.
 *
 * Dashboard surfaces describe a thing's health with one of six tokens rather
 * than with the grid's backend status, and this is the one place that turns a
 * token into a colour and paints it. Anything reporting the same token — a
 * card's rail header, the map's site detail panel — renders this, so a site
 * reads the same on the map as it does on its own dashboard.
 *
 * Contains:
 * - DashboardStatusDot — the dot (default export)
 * - dashboardStatusColor — one token's CSS colour
 */
import Box from '@mui/material/Box';
import React from 'react';

const DEFAULT_DOT_SIZE = 8;

/**
 * The colour one dashboard status token paints.
 *
 * @param {?string} status - a deriveSiteCardStatus token.
 * @param {?string} [defaultColor] - what an unrecognized token falls back to.
 * @returns {?string} a CSS colour.
 */
export function dashboardStatusColor(status, defaultColor) {
  switch (status) {
    case 'good':
      return 'var(--status-ok)';
    case 'bad':
      return 'var(--status-bad)';
    case 'warning':
      return 'var(--status-warning)';
    case 'purple':
      return 'var(--status-attention)';
    case 'info':
      return 'var(--status-info)';
    case 'disabled':
      return 'var(--status-neutral)';
    default:
      return defaultColor;
  }
}

/**
 * @param {object} props
 * @param {?string} props.color - the dot's fill, usually from `dashboardStatusColor`.
 * @param {number} [props.size] - diameter in pixels.
 * @param {string} [props.title] - hover/assistive text naming the status.
 * @param {object} [props.sx] - extra sx merged onto the dot.
 */
export default function DashboardStatusDot({ color, size = DEFAULT_DOT_SIZE, title, sx }) {
  if (!color) return null;
  return (
    <Box
      title={title}
      aria-label={title}
      sx={{ width: size, height: size, borderRadius: '50%', bgcolor: color, flexShrink: 0, ...sx }}
    />
  );
}
